/**
 * Console de la plateforme : session de plateforme, hors des entreprises.
 * `/admin/*` est servi par `AdminApp` (porte de session et coque) ; chaque
 * écran de la console a son adresse ici, une seule fois (refonte v4,
 * vague 4). Le serveur revérifie le jeton de plateforme à chaque requête.
 */
import { lazy, type ReactNode } from 'react';
import type { ExternalRoute } from '../../app/route-types.js';

const AdminApp = lazy(() => import('./AdminApp.js').then((m) => ({ default: m.AdminApp })));
const page = <K extends string>(load: () => Promise<Record<K, () => ReactNode>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));

const AdminHomePage = page(() => import('./AdminHomePage.js'), 'AdminHomePage');
const companies = () => import('./CompanyPages.js');
const CompaniesPage = page(companies, 'CompaniesPage');
const CompanyNewPage = page(companies, 'CompanyNewPage');
const CompanyPage = page(companies, 'CompanyPage');
const CompanyIdentityPage = page(companies, 'CompanyIdentityPage');
const CompanySubscriptionPage = page(companies, 'CompanySubscriptionPage');
const CompanyStoragePage = page(() => import('./CompanyStoragePage.js'), 'CompanyStoragePage');
const CompanyAiBudgetPage = page(() => import('./CompanyAiBudgetPage.js'), 'CompanyAiBudgetPage');
const CompanyOffboardPage = page(companies, 'CompanyOffboardPage');
const members = () => import('./MemberPages.js');
const MemberInvitePage = page(members, 'MemberInvitePage');
const MemberPage = page(members, 'MemberPage');
const MemberIdentityPage = page(members, 'MemberIdentityPage');
const support = () => import('./SupportPages.js');
const SupportPage = page(support, 'SupportPage');
const SupportRequestPage = page(support, 'SupportRequestPage');
const TicketNewPage = page(support, 'TicketNewPage');
const TicketPage = page(support, 'TicketPage');
const SupportSessionNewPage = page(support, 'SupportSessionNewPage');
const accountants = () => import('./AccountantPages.js');
const AccountantsPage = page(accountants, 'AccountantsPage');
const AccountantPage = page(accountants, 'AccountantPage');
const AccountantDecisionPage = page(accountants, 'AccountantDecisionPage');
const JobsPage = page(() => import('./JobsPage.js'), 'JobsPage');
const tools = () => import('./ToolsPages.js');
const ToolsPage = page(tools, 'ToolsPage');
const ReservedNamesPage = page(tools, 'ReservedNamesPage');
const ReservedNameNewPage = page(tools, 'ReservedNameNewPage');
const LegalDocumentsPage = page(tools, 'LegalDocumentsPage');
const LegalDocumentNewPage = page(tools, 'LegalDocumentNewPage');
const ReleasesPage = page(tools, 'ReleasesPage');
const PlatformJournalPage = page(tools, 'PlatformJournalPage');

export const superadminRoutes: ExternalRoute[] = [
  { path: '/admin/*', element: <AdminApp /> },
];

/** Pages de la console, relatives à `/admin`. */
export const ADMIN_PAGES: readonly { path: string; element: ReactNode }[] = [
  { path: '', element: <AdminHomePage /> },
  { path: 'entreprises', element: <CompaniesPage /> },
  { path: 'entreprises/nouvelle', element: <CompanyNewPage /> },
  { path: 'entreprises/:id', element: <CompanyPage /> },
  { path: 'entreprises/:id/identite', element: <CompanyIdentityPage /> },
  { path: 'entreprises/:id/abonnement', element: <CompanySubscriptionPage /> },
  { path: 'entreprises/:id/stockage', element: <CompanyStoragePage /> },
  { path: 'entreprises/:id/ia', element: <CompanyAiBudgetPage /> },
  { path: 'entreprises/:id/retrait', element: <CompanyOffboardPage /> },
  { path: 'entreprises/:id/ticket', element: <TicketNewPage /> },
  { path: 'entreprises/:id/assistance', element: <SupportSessionNewPage /> },
  { path: 'entreprises/:id/membres/invitation', element: <MemberInvitePage /> },
  { path: 'entreprises/:id/membres/:userId', element: <MemberPage /> },
  { path: 'entreprises/:id/membres/:userId/identite', element: <MemberIdentityPage /> },
  { path: 'assistance', element: <SupportPage /> },
  { path: 'assistance/demandes/:requestId', element: <SupportRequestPage /> },
  { path: 'assistance/tickets/nouveau', element: <TicketNewPage /> },
  { path: 'assistance/tickets/:ticketId', element: <TicketPage /> },
  { path: 'assistance/tickets/:ticketId/session', element: <SupportSessionNewPage /> },
  { path: 'comptables', element: <AccountantsPage /> },
  { path: 'comptables/:accountId', element: <AccountantPage /> },
  { path: 'comptables/:accountId/decision', element: <AccountantDecisionPage /> },
  { path: 'taches', element: <JobsPage /> },
  { path: 'outils', element: <ToolsPage /> },
  { path: 'outils/noms-reserves', element: <ReservedNamesPage /> },
  { path: 'outils/noms-reserves/nouveau', element: <ReservedNameNewPage /> },
  { path: 'outils/documents', element: <LegalDocumentsPage /> },
  { path: 'outils/documents/nouveau', element: <LegalDocumentNewPage /> },
  { path: 'outils/versions', element: <ReleasesPage /> },
  { path: 'outils/journal', element: <PlatformJournalPage /> },
];

/** Rubrique de la console ouverte, pour la navigation de la coque. */
export function adminSection(pathname: string): string {
  const first = pathname.split('?')[0]!.replace(/^\/admin\/?/, '').split('/')[0] ?? '';
  return ['entreprises', 'assistance', 'comptables', 'taches', 'outils'].includes(first) ? first : 'accueil';
}
